import { createSelector } from 'reselect';
import { getBrands } from '/@/store/public-data/selectors';
import { getAppInstance, getSessionCountry } from '/@/store/session/selectors';

// TO-DO: Update 'type' variable name to 'brandType' so it's clear that i comes from the URL.

// Selectors
export const getCatalogZoneId = (state) => state.catalog.zone;
export const getCatalogComparisons = (state) => state.catalog.comparisons;
export const getCatalogCropType = (state) => state.catalog.cropType;
export const getCatalogProductType = (state) => state.catalog.productType;

export const getCatalogEnabled = (state, catalogZones) => {
    const brands = getCatalogBrands(state);
    if (!brands.length) return false;

    return (
        brands.some((brand) => brand.type === 'crop_protection') ||
        brands.some((brand) => !!getCatalogZonesByBrand(state, brand, 'catalog', catalogZones).length)
    );
};

export const getCatalogBrands = createSelector([getAppInstance, getBrands], (appInstance, brands) =>
    brands.filter(({ id }) => appInstance.catalogBrands.includes(id))
);

export const getCatalogBrand = (state, crop) => {
    return getCatalogBrands(state).find(({ type }) => type === crop);
};

// One memoized selector per crop, cached lazily - getCatalogBrandsByCrop is called with a small,
// fixed set of crop values (corn/soy/crop_protection) from ~10 useSelector sites, so a plain
// createSelector (which only remembers its last call) would thrash and return a fresh array
// identity on every render, defeating useSelector's reference-equality bailout.
const catalogBrandsByCropSelectors: Record<string, ReturnType<typeof createSelector>> = {};
export const getCatalogBrandsByCrop = (state, crop) => {
    if (!catalogBrandsByCropSelectors[crop]) {
        catalogBrandsByCropSelectors[crop] = createSelector([getCatalogBrands], (brands) =>
            brands.filter(({ type }) => type === crop)
        );
    }
    return catalogBrandsByCropSelectors[crop](state);
};

export const getCatalogBrandBySlug = (state, inputSlug) => {
    return getCatalogBrands(state).find(({ slug }) => slug === inputSlug);
};

export const getCatalogZone = (state, catalogZones) => {
    const zone = getCatalogZoneId(state);
    return catalogZones.find(({ id }) => id === zone);
};

export const getCatalogZones = (state, crop, type = 'catalog', catalogZones) => {
    const zones = catalogZones.filter((catalogZone) => catalogZone.type === type);
    const country = getSessionCountry(state);
    return zones.filter((zone) => zone.countryId == country.id && zone.crop === crop);
};

export const getCatalogZonesByBrand = (state, brand, type = 'catalog', catalogZones) => {
    const zones = catalogZones.filter((catalogZone) => catalogZone.type === type);
    const country = getSessionCountry(state);

    return zones.filter((zone) => zone.countryId == country.id && zone.crop === brand.type);
};

export const getCatalogZonesByBrandWithCountry = (country, brand, type = 'catalog', catalogZones) => {
    const zones = catalogZones.filter((catalogZone) => catalogZone.type === type);

    return zones.filter((zone) => zone.countryId == country.id && zone.crop === brand.type);
};

export const getSelectedTechnology = (state, technologies) => {
    return state.catalog.technology ? technologies.find((t) => t.id === state.catalog.technology) : undefined;
};

export const getCropProtectionTargetSearch = (state) => state.catalog.cropProtectionTargetSearch;

export const getCatalogSortField = (state) => state.catalog.sortField;
